import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, TransitionEvent } from 'react';

import { useLocation } from 'react-router-dom';

import { ResizeHandle } from '@shared/components/layout/ResizeHandle';
import { Icons } from '@shared/components/ui';
import { Z } from '@shared/constants/zIndex';
import { useViewport } from '@shared/hooks/useViewport';
import { useViewportWidth } from '@shared/hooks/useViewportWidth';

import { FRAME_BTN } from './chatDockConstants';
import { DockAskSelection } from './DockAskSelection';
import { DockContextChip } from './DockContextChip';
import { useChatDockRegistry, selectActiveDescriptor } from './useChatDockRegistry';
import { useChatDockStore } from './useChatDockStore';
import { useDockResize } from './useDockResize';

import type { ChatSurfaceDescriptor } from './chatDockTypes';

const DOCK_EXCLUDED = (path: string) => path.startsWith('/chat');

/**
 * Resolve the expanded overlay's width: the whole viewport.
 *
 * It previously kept an 80px gutter and capped at 1200px, which on a 1920-wide
 * screen left the panel at 63% — wide enough to hide the page behind it, not
 * wide enough to feel like the chat. Expanded is a reading mode now: it takes
 * the screen, and `ChatShellPanel`'s reading column is what keeps the text
 * readable at that width.
 *
 * Still a plain px number — CSS transitions interpolate the raw `width`, so a
 * `calc`/`min` with `vw` (or a `max-width` cap) animates from an unexpected
 * resolved value and visibly jumps wider before collapsing/closing.
 */
const resolveExpandedWidth = (viewportWidth: number): number => viewportWidth;

/**
 * Resolve whether the dock is active for the current route + registered surface,
 * and run the side effect (hydrate persisted geometry).
 * Extracted from the component to keep each unit's complexity low.
 */
function useActiveDock(): { active: boolean; descriptor: ChatSurfaceDescriptor | null } {
  const location = useLocation();
  const descriptor = useChatDockRegistry(selectActiveDescriptor);
  const hydrate = useChatDockStore(s => s.hydrate);

  const active = !!descriptor && !DOCK_EXCLUDED(location.pathname);

  useEffect(() => {
    if (!active || !descriptor) return;
    hydrate(descriptor.persistKey ?? descriptor.key, descriptor.defaultOpen ?? false);
  }, [active, descriptor, hydrate]);

  return { active, descriptor };
}

/**
 * The dock's external portal target.
 *
 * Every chat surface is now `external`: its body is rendered by the consumer's
 * `<DockSlot>` and portaled into this target. The dock owns only the target div
 * and its open/hidden visibility — the keep-mounted (non-default) vs lazy
 * (default-floor) mounting strategy lives entirely in `DockSlot`, which gates the
 * consumer-rendered body.
 *
 * `min-h-0 flex-1` (when open) makes the portaled body fill the aside's remaining
 * height (the aside is `flex h-full flex-col`; the controls row above is shrink-0).
 * Without it the body's `h-full`/scroll container has no bounded height and a long
 * chat grows past the viewport with no scrollbar (lost below the browser bottom).
 * `hidden` (when closed) keeps the target mounted so its published element stays
 * stable while invisible.
 */
function DockColumnBody({ open }: { open: boolean }) {
  const setPortalTarget = useChatDockStore(s => s.setPortalTarget);
  // Stable callback ref so it fires only on mount/unmount, not on every
  // open/width/expanded re-render (an inline ref would write the store twice each render).
  // The callback ref publishes the element (and clears it to null on unmount).
  const portalRef = useCallback((el: HTMLElement | null) => setPortalTarget(el), [setPortalTarget]);
  return (
    <div
      data-testid="dock-portal-target"
      ref={portalRef}
      className={open ? 'min-h-0 flex-1' : 'hidden'}
    />
  );
}

/**
 * Compute the `<aside>` className + inline style for the dock's presentations:
 * mobile overlay (full-screen), desktop expanded (large fixed overlay, out of flex
 * flow so main content un-pushes), the transient collapse (fixed overlay shrinking
 * to docked width so main content stays static), and desktop push-column (animated
 * width). Extracted to keep `ChatDockColumn`'s complexity low.
 */
function getDockColumnPresentation({
  overlay,
  open,
  desktopExpanded,
  collapsing,
  resizing,
  width,
  expandedWidth,
}: {
  overlay: boolean;
  open: boolean;
  desktopExpanded: boolean;
  collapsing: boolean;
  resizing: boolean;
  width: number;
  expandedWidth: number;
}): { className: string; style: CSSProperties; resizable: boolean } {
  // The aside switches presentation WITHOUT remounting (stable key) so the body's
  // in-flight state survives an expand toggle (keep-mounted contract).
  if (overlay) {
    // Mobile: full-screen overlay when open; hidden (display:none) when closed so
    // the body stays MOUNTED (in-flight state + Home's client transcript survive a
    // close) but is invisible and out of hit-testing — mirrors desktop keep-mounted.
    return open
      ? {
          className: 'fixed inset-0 flex flex-col bg-background',
          style: { zIndex: Z.MODAL },
          resizable: false,
        }
      : { className: 'hidden', style: {}, resizable: false };
  }
  if (desktopExpanded || collapsing) {
    // Both the expanded overlay and the collapse animation stay `fixed` (out of
    // flex flow) so the main document keeps its width and never reflows. Expanded
    // sits at near-full width; collapsing animates that width toward its resting
    // size — the docked width when collapsing to the column, or 0 when closing
    // from expanded — after which `ChatDockColumn` settles the aside back into
    // (or out of) the flex column behind the already-resized chat.
    // `transition-[width]` (not `transition-all`) so only width animates — the
    // position/shadow snap instantly rather than sliding the layout. All three
    // widths (expanded / docked / closed) are plain px so the transition between
    // them interpolates predictably — see `resolveExpandedWidth` for why px and
    // not `calc(100vw - …)` + `max-width`.
    return {
      className:
        'bg-background fixed inset-y-0 right-0 flex flex-col overflow-hidden border-l border-border shadow-2xl transition-[width] duration-300 ease-in-out',
      style: desktopExpanded
        ? { zIndex: Z.MODAL, width: expandedWidth }
        : { zIndex: Z.MODAL, width: open ? width : 0 },
      resizable: false,
    };
  }
  // Only this in-flow docked column resizes. `relative` anchors the handle; the
  // width transition is dropped while dragging so the column tracks the cursor.
  return {
    className: `relative flex h-full flex-col overflow-hidden bg-background ${
      resizing ? '' : 'transition-[width] duration-300 ease-in-out'
    } ${open ? 'border-l border-border' : ''}`,
    style: { width: open ? width : 0 },
    resizable: open,
  };
}

/** The docked column's left-edge drag handle; renders nothing when the dock can't resize. */
function DockResizeEdge({
  enabled,
  resize,
}: {
  enabled: boolean;
  resize: ReturnType<typeof useDockResize>;
}) {
  if (!enabled) return null;
  return (
    <ResizeHandle
      side="left"
      onMouseDown={resize.handleMouseDown}
      onDoubleClick={resize.handleDoubleClick}
    />
  );
}

const FRAME_ROW = 'flex shrink-0 items-center justify-between px-2 pt-2';

/** The header row's left slot: the surface's context chip, or an empty span so
 *  the right-aligned buttons keep their place when a surface has no context. */
function DockContextSlot({ descriptor }: { descriptor: ChatSurfaceDescriptor }) {
  return descriptor.context ? <DockContextChip context={descriptor.context} /> : <span />;
}

/**
 * Desktop-only frame control row: a thin row above the body with the context
 * chip on the left and the Expand/Compress toggle + Close on the right. Its OWN
 * row (not absolutely positioned) so it never overlaps the chat panel's
 * right-aligned header actions.
 */
function DockFrameControls({
  descriptor,
  expanded,
  onToggleExpanded,
  onClose,
}: {
  descriptor: ChatSurfaceDescriptor;
  expanded: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
}) {
  return (
    <div className={FRAME_ROW}>
      <DockContextSlot descriptor={descriptor} />
      <div className="flex items-center gap-0.5">
        <DockAskSelection />
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-label={expanded ? 'Collapse assistant' : 'Expand assistant'}
          className={FRAME_BTN}
        >
          {expanded ? <Icons.Compress size="sm" /> : <Icons.Expand size="sm" />}
        </button>
        {/* Direct close — when expanded, the overlay (z=MODAL) covers the global
            toggle, so a frame close is the only one-click way to close. */}
        <button type="button" onClick={onClose} aria-label="Close assistant" className={FRAME_BTN}>
          <Icons.X size="sm" />
        </button>
      </div>
    </div>
  );
}

/** The mobile overlay's header: the context chip, Ask-about-selection when there
 *  is one, and a Close button (no expand — the overlay is already full-screen).
 *  Its own row, like the desktop frame, so the chip has a place to sit instead
 *  of a lone absolutely positioned button. */
function DockOverlayHeader({
  descriptor,
  onClose,
}: {
  descriptor: ChatSurfaceDescriptor;
  onClose: () => void;
}) {
  return (
    <div className={FRAME_ROW}>
      <DockContextSlot descriptor={descriptor} />
      <div className="flex items-center gap-0.5">
        <DockAskSelection />
        <button type="button" onClick={onClose} aria-label="Close assistant" className={FRAME_BTN}>
          <Icons.X size="sm" />
        </button>
      </div>
    </div>
  );
}

/**
 * Drive the desktop overlay's transient collapse/close animation.
 *
 * Leaving the expanded overlay (frame toggle, backdrop, or close) keeps the aside
 * `fixed` (out of flex flow, so the main document never reflows) while its width
 * animates to its resting size — the docked width when collapsing to the column,
 * or 0 when closing. The `collapsing` latch and the store flip commit in the same
 * render (no in-flow squeeze frame) and the aside settles back into/out of the
 * flex column on the width `transitionend`, with a 350ms timer as a safety net for
 * environments where the event never fires (reduced motion, interrupted
 * transitions). Driving this from handlers rather than a layout effect keeps it
 * free of `setState`-in-effect.
 */
function useDockCollapse({
  open,
  desktopExpanded,
  toggle,
  toggleExpanded,
}: {
  open: boolean;
  desktopExpanded: boolean;
  toggle: () => void;
  toggleExpanded: () => void;
}) {
  const [collapsing, setCollapsing] = useState(false);

  const handleToggleExpanded = () => {
    setCollapsing(desktopExpanded); // animate only when leaving the expanded overlay
    toggleExpanded();
  };
  const handleClose = () => {
    setCollapsing(desktopExpanded);
    toggle();
  };
  // Settle once the aside's OWN width transition ends (`target === currentTarget`)
  // so transitionend events bubbling up from the chat body don't settle it early.
  const onAsideTransitionEnd = (e: TransitionEvent<HTMLElement>) => {
    if (collapsing && e.target === e.currentTarget) setCollapsing(false);
  };

  useEffect(() => {
    if (!collapsing) return;
    const t = setTimeout(() => setCollapsing(false), 350);
    return () => clearTimeout(t);
  }, [collapsing]);

  // Keep the body mounted/visible while the panel shrinks shut so the chat slides
  // away with it rather than blinking out before the animation.
  return {
    collapsing,
    bodyOpen: open || collapsing,
    handleToggleExpanded,
    handleClose,
    onAsideTransitionEnd,
  };
}

/** The full-height dock column: slides open (animating width) on desktop and
 *  renders as a full-screen overlay on mobile. No collapsed rail — the
 *  ChatEdgeHandle (desktop) and GlobalChatToggle (phone) are the openers. */
function ChatDockColumn({ descriptor }: { descriptor: ChatSurfaceDescriptor }) {
  const open = useChatDockStore(s => s.open);
  const width = useChatDockStore(s => s.width);
  const toggle = useChatDockStore(s => s.toggle);
  const expanded = useChatDockStore(s => s.expanded);
  const toggleExpanded = useChatDockStore(s => s.toggleExpanded);
  const setBodyOpen = useChatDockStore(s => s.setBodyOpen);
  const isDesktop = useViewport() !== 'phone';

  const overlay = !isDesktop;
  const viewportWidth = useViewportWidth();
  const expandedWidth = resolveExpandedWidth(viewportWidth);

  // NOTE: unlike before, mobile-closed does NOT early-return null — the aside
  // renders `hidden` (see getDockColumnPresentation) so non-default bodies stay
  // mounted across a close (keep-mounted). The default surface remains lazy.
  const desktopExpanded = open && expanded && !overlay;

  // Transient collapse/close animation: keep the aside a `fixed` overlay (out of
  // flex flow, document stays static) and animate its width to the resting size
  // instead of re-entering the flex row at full width and squeezing the document.
  const { collapsing, bodyOpen, handleToggleExpanded, handleClose, onAsideTransitionEnd } =
    useDockCollapse({ open, desktopExpanded, toggle, toggleExpanded });

  // Publish the collapse-aware open value so `<DockSlot>` can gate a default-floor
  // external child on it (stays mounted through the close animation, unmounts after).
  // Reset to false on unmount so a stale `true` can't keep a floor child mounted.
  useEffect(() => {
    setBodyOpen(bodyOpen);
  }, [bodyOpen, setBodyOpen]);
  useEffect(() => () => setBodyOpen(false), [setBodyOpen]);

  const asideRef = useRef<HTMLElement>(null);
  const resize = useDockResize(asideRef);

  const { className, style, resizable } = getDockColumnPresentation({
    overlay,
    open,
    desktopExpanded,
    collapsing,
    resizing: resize.isResizing,
    width,
    expandedWidth,
  });

  return (
    <>
      {(desktopExpanded || collapsing) && (
        <div
          key="chatdock-backdrop"
          data-testid="chatdock-backdrop"
          className={`fixed inset-0 bg-[var(--modal-backdrop)] transition-opacity duration-300 ${
            collapsing ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ zIndex: Z.OVERLAY }}
          onClick={handleToggleExpanded}
          aria-hidden="true"
        />
      )}
      <aside
        key="chatdock-aside"
        ref={asideRef}
        aria-label="Assistant"
        className={className}
        style={style}
        onTransitionEnd={onAsideTransitionEnd}
      >
        <DockResizeEdge enabled={resizable} resize={resize} />
        {open && !overlay && (
          <DockFrameControls
            descriptor={descriptor}
            expanded={expanded}
            onToggleExpanded={handleToggleExpanded}
            onClose={handleClose}
          />
        )}
        {open && overlay && <DockOverlayHeader descriptor={descriptor} onClose={toggle} />}
        <DockColumnBody open={bodyOpen} />
      </aside>
    </>
  );
}

/** The single, app-shell-level chat dock. Activates only when a surface
 *  descriptor is registered (gating); hidden on chat routes. */
export function ChatDock() {
  const { active, descriptor } = useActiveDock();
  if (!active || !descriptor) return null;
  return <ChatDockColumn descriptor={descriptor} />;
}
